import { useEffect, useState, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useGlobalContext } from "../context/GlobalContext.jsx";
import { isTest } from "../config/environment.js";
import requestHandler from "../utils/requestHandler.js";
import {
  offsetInTableRange,
  firstTableStartWithin,
  findValidSegment,
} from "../utils/highlightGeometry.js";
import {
  BorderBox,
  Content,
  UtilityModal,
  Notes,
  NotesModal,
  UtilityPanel,
  Spinner,
} from "../components/index.js";

export default function Monogrpah() {
  const { lessonId } = useParams();
  const location = useLocation();
  const { highlightColor, fontSize, darkMode } = useGlobalContext();

  const [modalOpen, setModalOpen] = useState(false);
  const [notesPanelOpen, setNotesPanelOpen] = useState(false);
  const [currentHighlightKey, setCurrentHighlightKey] = useState(null);
  // Bumped only when the server reconcile replaces our annotation set, forcing
  // a single full repaint. It is NOT touched by ordinary add/delete/recolor —
  // those mutate the DOM incrementally (see addHighlight/removeHighlightFromDom).
  const [rebuildSignal, setRebuildSignal] = useState(0);
  const [currentHighlight, setCurrentHighlight] = useState(null);
  const contentRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [currentNote, setCurrentNote] = useState("");
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const [loading, setLoading] = useState(true);
  const [lessonContent, setLessonContent] = useState(null);
  const [annotations, setAnnotations] = useState({});
  const [archive, setArchive] = useState([]);
  const [pendingUpdates, setPendingUpdates] = useState(false);
  const updateTimeoutRef = useRef(null);
  const isInitialLoad = useRef(true);
  const isInitialArchiveLoad = useRef(true);
  const processedHighlightRef = useRef(null);
  const processedNoteScrollRef = useRef(null);
  // Live mirror of annotations so async/debounced callbacks and the single
  // delegated event handler always read the latest set without re-binding.
  const annotationsRef = useRef(annotations);
  annotationsRef.current = annotations;
  // Character ranges [{start,end}) covered by <table> elements, recomputed once
  // per content load. Used to keep highlights from extending into tables.
  const tableRangesRef = useRef([]);

  const getLesson = async (lessonId) => {
    setLoading(true);
    const res = await requestHandler({
      path: isTest() ? "lessons-drafts" : "lessons",
      method: "POST",
      body: { slug: lessonId },
    });

    if (res.success) {
      setLessonContent(res.data);
      // Initialize annotations from API response
      if (res.data.annotations) {
        setAnnotations(JSON.parse(res.data.annotations) || {});
      } else {
        setAnnotations({});
      }
      // Initialize archive from API response
      setArchive(
        res.data.archives
          ? res.data.archives.map((blob) => JSON.parse(blob))
          : [],
      );
      isInitialArchiveLoad.current = true;
    } else {
      console.error("Error fetching lesson:", res.error);
      // Handle 403 forbidden if needed
      // if (res.status === 403) {
      //   navigate("/forbidden", { state: { message: res.error } });
      // }
    }
    setLoading(false);
  };

  useEffect(() => {
    getLesson(lessonId);
    // Reset initial load flag when navigating to new lesson
    isInitialLoad.current = true;
    processedHighlightRef.current = null;
    processedNoteScrollRef.current = null;
    // Reset parent scroll container immediately on lesson change (Safari fix)
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [lessonId]);

  // Value-aware comparison so a server echo of the same set does not re-enter
  // the apply cycle. Compares the fields we actually persist.
  const annotationsEqual = (a, b) => {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((key) => {
      const av = a[key];
      const bv = b[key];
      if (!bv) return false;
      return (
        av.start === bv.start &&
        av.end === bv.end &&
        av.color === bv.color &&
        (av.note || "") === (bv.note || "")
      );
    });
  };

  // Debounced annotation updates to server
  const updateAnnotationsOnServer = () => {
    // Clear any pending timeout
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    setPendingUpdates(true);

    // Set a new timeout to update after 1 second of inactivity
    updateTimeoutRef.current = setTimeout(async () => {
      try {
        // Send a snapshot of the CURRENT annotations (read from the live ref so
        // the debounced send is never a stale closure).
        const annotationsSnapshot = { ...annotationsRef.current };

        const response = await requestHandler({
          path: "annotate",
          method: "POST",
          body: {
            slug: lessonId,
            annotations: JSON.stringify(annotationsSnapshot),
            archives: archive.map((blob) => JSON.stringify(blob)),
          },
        });

        // If the server returns a genuinely different set, reconcile once.
        if (response.success && response.data && response.data.annotations) {
          try {
            const serverAnnotations = JSON.parse(response.data.annotations);
            if (
              serverAnnotations &&
              Object.keys(serverAnnotations).length > 0 &&
              !annotationsEqual(annotationsRef.current, serverAnnotations)
            ) {
              setAnnotations(serverAnnotations);
              // One explicit repaint for the reconciled set.
              setRebuildSignal((prev) => prev + 1);
            }
          } catch (parseError) {
            console.error("Error parsing server annotations:", parseError);
          }
        }
      } catch (error) {
        console.error("Error updating annotations:", error);
      } finally {
        setPendingUpdates(false);
      }
    }, 1000);
  };

  // Update server whenever annotations change
  useEffect(() => {
    // Skip the initial render when annotations are loaded from API
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }

    // Only update if we have annotations or if we're explicitly clearing them
    updateAnnotationsOnServer();

    // Cleanup function to ensure any pending updates are sent
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
        if (pendingUpdates) {
          // Capture the latest annotations for the final flush.
          const finalAnnotations = { ...annotationsRef.current };
          requestHandler({
            path: "annotate",
            method: "POST",
            body: {
              slug: lessonId,
              annotations: JSON.stringify(finalAnnotations),
              archives: archive.map((blob) => JSON.stringify(blob)),
            },
          }).catch((error) => {
            console.error("Error updating annotations on cleanup:", error);
          });
        }
      }
    };
    // Intentionally keyed only on `annotations`: this drives the debounced
    // server save. Adding the other referenced values would re-arm the debounce
    // spuriously. Latest values are read via refs inside the timeout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotations]);

  const deleteArchivedAnnotation = (blobIndex, annotationId) => {
    setArchive((prev) => {
      const updated = prev.map((blob, i) => {
        if (i !== blobIndex) return blob;
        const newBlob = { ...blob };
        delete newBlob[annotationId];
        return newBlob;
      });
      // Filter out empty blobs
      return updated.filter((blob) => Object.keys(blob).length > 0);
    });
  };

  // Persist archive changes to server
  useEffect(() => {
    if (isInitialArchiveLoad.current) {
      isInitialArchiveLoad.current = false;
      return;
    }

    requestHandler({
      path: "annotate",
      method: "POST",
      body: {
        slug: lessonId,
        annotations: JSON.stringify(annotations),
        archives: archive.map((blob) => JSON.stringify(blob)),
      },
    }).catch((error) => {
      console.error("Error updating archive:", error);
    });
    // Intentionally keyed only on `archive`: this persists archive changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archive]);

  const getSelectColor = (selectColor) => {
    return selectColor === "yellow"
      ? "selection:bg-lz-yellow"
      : selectColor === "green"
        ? "selection:bg-lz-light-green"
        : selectColor === "pink"
          ? "selection:bg-lz-light-pink"
          : "";
  };

  const getHighlightBgClass = (color) =>
    color === "yellow"
      ? "bg-lz-yellow"
      : color === "green"
        ? "bg-lz-light-green"
        : color === "pink"
          ? "bg-lz-light-pink"
          : "";

  const HIGHLIGHT_BG_CLASSES = [
    "bg-lz-yellow",
    "bg-lz-light-green",
    "bg-lz-light-pink",
  ];

  // Strip every highlight span, restoring clean text. Used only by the full
  // rebuild path (content load / lesson change / server reconcile).
  const cleanupHighlights = () => {
    if (contentRef.current) {
      const content = contentRef.current;
      const spans = content.querySelectorAll("span[data-highlight-id]");
      spans.forEach((span) => {
        const text = span.textContent;
        const textNode = document.createTextNode(text);
        span.parentNode?.replaceChild(textNode, span);
      });
    }
  };

  // Remove a single highlight's spans in place (no full rebuild) and merge the
  // surrounding text nodes back together.
  const removeHighlightFromDom = (key) => {
    if (!contentRef.current) return;
    const spans = contentRef.current.querySelectorAll(
      `span[data-highlight-id="${key}"]`,
    );
    spans.forEach((span) => {
      const parent = span.parentNode;
      if (!parent) return;
      parent.replaceChild(document.createTextNode(span.textContent), span);
      parent.normalize();
    });
  };

  // Swap the background color class on a highlight's spans in place.
  const recolorHighlightInDom = (key, newColor) => {
    if (!contentRef.current) return;
    const spans = contentRef.current.querySelectorAll(
      `span[data-highlight-id="${key}"]`,
    );
    const cls = getHighlightBgClass(newColor);
    spans.forEach((span) => {
      span.classList.remove(...HIGHLIGHT_BG_CLASSES);
      if (cls) span.classList.add(cls);
      span.dataset.color = newColor;
    });
  };

  const deleteHighlight = (key) => {
    if (key) {
      setAnnotations((prevAnnotations) => {
        const newAnnotations = { ...prevAnnotations };
        delete newAnnotations[key];
        return newAnnotations;
      });
      removeHighlightFromDom(key);
      setModalOpen(false);
    }
  };

  const updateHighlight = (key, newColor, note) => {
    if (key) {
      setAnnotations((prevAnnotations) => {
        const newAnnotations = { ...prevAnnotations };
        if (newAnnotations[key]) {
          newAnnotations[key] = {
            ...newAnnotations[key],
            color: newColor,
            note: note || newAnnotations[key].note,
          };
        }
        return newAnnotations;
      });
      recolorHighlightInDom(key, newColor);
      setModalOpen(false);
    }
  };

  const saveNote = (note) => {
    if (currentHighlightKey) {
      setAnnotations((prevAnnotations) => {
        const newAnnotations = { ...prevAnnotations };
        if (newAnnotations[currentHighlightKey]) {
          newAnnotations[currentHighlightKey] = {
            ...newAnnotations[currentHighlightKey],
            note,
          };
        }
        return newAnnotations;
      });
      setNotesModalOpen(false);
      setModalOpen(false);
    }
  };

  const deleteNote = (highlightKey) => {
    setAnnotations((prevAnnotations) => {
      const newAnnotations = { ...prevAnnotations };
      if (newAnnotations[highlightKey]) {
        newAnnotations[highlightKey] = {
          ...newAnnotations[highlightKey],
          note: "",
        };
      }
      return newAnnotations;
    });
  };

  const scrollToHighlight = (highlightId) => {
    const highlightSpan = document.querySelector(
      `[data-highlight-id="${highlightId}"]`,
    );
    if (highlightSpan) {
      highlightSpan.scrollIntoView({ behavior: "smooth", block: "center" });

      // Add visual feedback - glowing aura around all spans with this highlight ID
      const allSpans = document.querySelectorAll(
        `[data-highlight-id="${highlightId}"]`,
      );
      const shadowColor = darkMode
        ? "rgba(255, 255, 255, 0.7)"
        : "rgba(15, 7, 49, 0.7)";
      const shadowColorOuter = darkMode
        ? "rgba(255, 255, 255, 0.4)"
        : "rgba(15, 7, 49, 0.4)";
      allSpans.forEach((span) => {
        span.style.transition = "box-shadow 0.5s ease-out";
        span.style.boxShadow = `0 0 12px 4px ${shadowColor}, 0 0 20px 8px ${shadowColorOuter}`;
      });
      setTimeout(() => {
        allSpans.forEach((span) => {
          span.style.boxShadow = "none";
        });
        // Clean up transition style after fade completes
        setTimeout(() => {
          allSpans.forEach((span) => {
            span.style.transition = "";
          });
        }, 500);
      }, 1500);
    }
  };

  const getCharacterCounts = (range) => {
    if (!contentRef.current) return;
    const content = contentRef.current;

    // Offsets are measured in the content's plain text. Highlight spans do not
    // add characters, so this is stable whether or not highlights are present.
    const preSelectionRange = range.cloneRange();
    preSelectionRange.selectNodeContents(content);
    preSelectionRange.setEnd(range.startContainer, range.startOffset);

    const startOffset = preSelectionRange.toString().length;
    const selectedText = range.toString();
    const endOffset = startOffset + selectedText.length;

    return { start: startOffset, end: endOffset };
  };

  const getAllTextNodes = (element) => {
    const textNodes = [];
    const walk = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      null,
      false,
    );
    let node;
    while ((node = walk.nextNode())) {
      textNodes.push(node);
    }
    return textNodes;
  };

  const createRangeFromOffsets = (start, end) => {
    if (!contentRef.current) return;
    const content = contentRef.current;

    // Walk the live text nodes, summing lengths to locate the start/end nodes.
    // Spans never change character counts, so this maps offsets correctly even
    // when highlights are already present.
    const textNodes = getAllTextNodes(content);
    let currentOffset = 0;
    let startNode = null;
    let endNode = null;
    let localStartOffset = 0;
    let localEndOffset = 0;

    for (let node of textNodes) {
      const nodeLength = node.textContent.length;

      if (currentOffset + nodeLength > start && !startNode) {
        startNode = node;
        localStartOffset = start - currentOffset;
      }

      if (currentOffset + nodeLength >= end && !endNode) {
        endNode = node;
        localEndOffset = end - currentOffset;
        break;
      }

      currentOffset += nodeLength;
    }

    if (!startNode || !endNode) {
      throw new Error("Invalid range");
    }

    const range = document.createRange();
    range.setStart(startNode, localStartOffset);
    range.setEnd(endNode, localEndOffset);
    return range;
  };

  const handleHighlightClick = (highlight, key) => {
    // Only handle clicks on desktop devices (width >= 1024px)
    if (window.innerWidth >= 1024) {
      const current = annotationsRef.current[key];
      setCurrentHighlightKey(key);
      setCurrentHighlight({
        ...highlight,
        note: current?.note || "",
        timestamp: current?.timestamp || "",
      });
      setModalOpen(true);
    }
  };

  const applyHighlight = (range, color, key) => {
    try {
      const containedElements = [];
      let currentNode = range.startContainer;
      const endNode = range.endContainer;

      // Helper function to create highlight span. Interaction is handled by a
      // single delegated listener on the content container (see the effect
      // below), so spans carry only data + classes — no per-span listeners.
      const createHighlightSpan = () => {
        const span = document.createElement("span");
        const bg = getHighlightBgClass(color);
        span.className =
          (bg ? bg + " " : "") +
          "text-black cursor-pointer rounded-sm hover:shadow-lz-dark";

        span.dataset.highlightId = key;
        span.dataset.color = color;
        span.dataset.start = range.startOffset;
        span.dataset.end = range.endOffset;
        return span;
      };

      // Find all nodes between start and end
      while (currentNode) {
        if (currentNode.nodeType === Node.TEXT_NODE) {
          containedElements.push(currentNode);
        }

        if (currentNode === endNode) break;

        currentNode = getNextTextNode(currentNode);
      }

      // Apply highlights to each text node
      containedElements.forEach((node, index) => {
        const newRange = document.createRange();

        if (index === 0) {
          // First node
          newRange.setStart(
            node,
            range.startContainer === node ? range.startOffset : 0,
          );
          newRange.setEnd(
            node,
            node === endNode ? range.endOffset : node.length,
          );
        } else if (index === containedElements.length - 1) {
          // Last node
          newRange.setStart(node, 0);
          newRange.setEnd(node, range.endOffset);
        } else {
          // Middle nodes
          newRange.setStart(node, 0);
          newRange.setEnd(node, node.length);
        }

        const span = createHighlightSpan();
        newRange.surroundContents(span);
      });
    } catch (e) {
      console.warn("Failed to apply highlight:", e);
    }
  };

  const getNextTextNode = (node) => {
    // Try to get the next sibling
    let nextNode = node;

    while (nextNode) {
      // Try to move to the next sibling
      if (nextNode.nextSibling) {
        nextNode = nextNode.nextSibling;
        // If this is a text node, return it
        if (nextNode.nodeType === Node.TEXT_NODE) {
          return nextNode;
        }
        // If it's an element, try to get its first text child
        const firstText = getFirstTextNode(nextNode);
        if (firstText) {
          return firstText;
        }
      } else {
        // No more siblings, move up to parent's next sibling
        nextNode = nextNode.parentNode;
        if (!nextNode || nextNode.id === "content") {
          return null;
        }
      }
    }
    return null;
  };

  const getFirstTextNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node;
    }

    for (let child of node.childNodes) {
      const textNode = getFirstTextNode(child);
      if (textNode) {
        return textNode;
      }
    }

    return null;
  };

  const findLastWordBeforeTable = (startOffset) => {
    if (!contentRef.current) return startOffset;

    const text = contentRef.current.textContent;
    let currentOffset = startOffset;

    // Move backwards until we find a word boundary
    while (currentOffset > 0 && /\S/.test(text[currentOffset - 1])) {
      currentOffset--;
    }

    return currentOffset;
  };

  // Recompute the character ranges covered by <table> elements. Done once per
  // content load (cheap single text-node walk) so per-selection table checks
  // are O(tables) instead of O(selection-length x elements).
  const computeTableRanges = () => {
    const ranges = [];
    if (!contentRef.current) {
      tableRangesRef.current = ranges;
      return;
    }

    const textNodes = getAllTextNodes(contentRef.current);
    let currentOffset = 0;
    let openStart = null;
    let openEnd = 0;

    textNodes.forEach((node) => {
      const len = node.textContent.length;
      const inTable = !!(
        node.parentElement && node.parentElement.closest("table")
      );
      if (inTable) {
        if (openStart === null) openStart = currentOffset;
        openEnd = currentOffset + len;
      } else if (openStart !== null) {
        ranges.push({ start: openStart, end: openEnd });
        openStart = null;
      }
      currentOffset += len;
    });
    if (openStart !== null) ranges.push({ start: openStart, end: openEnd });

    tableRangesRef.current = ranges;
  };

  const handleHighlightSelection = (selection, expandedRange) => {
    const characterCounts = getCharacterCounts(expandedRange);
    if (!characterCounts) return;

    // Reject selections that start inside a table.
    if (offsetInTableRange(characterCounts.start, tableRangesRef.current)) {
      selection.removeAllRanges();
      return;
    }

    // If the selection runs into a table, trim it back to the last word before
    // the table begins (O(tables) using the precomputed ranges).
    const tableStart = firstTableStartWithin(
      characterCounts.start,
      characterCounts.end,
      tableRangesRef.current,
    );
    if (tableStart !== null) {
      characterCounts.end = findLastWordBeforeTable(tableStart);
    }

    if (characterCounts.end <= characterCounts.start) {
      selection.removeAllRanges();
      return;
    }

    const validSegment = findValidSegment(
      characterCounts.start,
      characterCounts.end,
      Object.values(annotations),
    );

    if (!validSegment) {
      selection.removeAllRanges();
      return;
    }

    // Generate a completely unique ID using a timestamp plus random suffix
    // This ensures IDs will never collide even with server-side race conditions
    const timestamp = Date.now();
    const randomSuffix = Math.floor(Math.random() * 1000);
    const newId = parseInt(`${timestamp}${randomSuffix}`.substring(0, 15), 10);

    // Create a new range using the valid segment coordinates
    const validRange = createRangeFromOffsets(
      validSegment.start,
      validSegment.end,
    );

    const data = {
      ...validSegment,
      color: highlightColor,
      timestamp: new Date().toISOString(),
      text: validRange ? validRange.toString() : "",
    };

    // Update the annotations state (drives debounced server persistence).
    setAnnotations((prevAnnotations) => ({
      ...prevAnnotations,
      [newId]: data,
    }));

    // Paint ONLY the new highlight. No full cleanup/reapply of the others —
    // this is what keeps highlight creation O(1) instead of O(N) per add.
    if (validRange) {
      applyHighlight(validRange, highlightColor, newId.toString());
    }

    selection.removeAllRanges();
  };

  // Full repaint of all highlights from the annotation set. Used only on
  // content load, lesson change, or an explicit server reconcile.
  const rebuildAllHighlights = () => {
    if (!contentRef.current) return;
    cleanupHighlights();
    const highlights = Object.entries(annotationsRef.current)
      .map(([key, value]) => ({ key, ...value }))
      .sort((a, b) => a.start - b.start);

    for (const highlight of highlights) {
      try {
        const range = createRangeFromOffsets(highlight.start, highlight.end);
        if (range) {
          applyHighlight(range, highlight.color, highlight.key);
        }
      } catch (e) {
        console.error("Failed to apply highlight:", e);
      }
    }
  };

  // Single full build: runs when the lesson content becomes ready, the lesson
  // changes, or the server reconcile bumps rebuildSignal. Crucially it does NOT
  // depend on `annotations`, so adding a highlight never triggers a rebuild.
  useEffect(() => {
    if (!contentRef.current || loading || !lessonContent) return;

    computeTableRanges();

    // Defer to the next tick so the content's innerHTML is in the DOM.
    const timeoutId = setTimeout(() => {
      rebuildAllHighlights();

      // If navigated from Journal with a highlight ID, scroll to it.
      if (
        location.state?.highlightId &&
        location.state.highlightId !== processedHighlightRef.current
      ) {
        processedHighlightRef.current = location.state.highlightId;
        setTimeout(() => {
          scrollToHighlight(location.state.highlightId);
        }, 100);
      }

      // If navigated from Journal by clicking a final note, scroll to the
      // notes section at the bottom of the lesson (once per navigation).
      if (
        location.state?.scrollToNote &&
        processedNoteScrollRef.current !== location.key
      ) {
        processedNoteScrollRef.current = location.key;
        setTimeout(() => {
          document
            .getElementById("final-notes-anchor")
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      }
    }, 0);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, lessonContent, loading, rebuildSignal, location.key]);

  // One delegated listener set for all highlight interaction, attached to the
  // content container. Replaces 3 listeners per span (which never scaled and
  // leaked on cleanup). Reads annotations via the ref, so it never goes stale.
  useEffect(() => {
    const content = contentRef.current;
    if (!content || loading || !lessonContent) return;

    const groupSpans = (key) =>
      content.querySelectorAll(`[data-highlight-id="${key}"]`);

    const handleClick = (e) => {
      const span = e.target.closest("[data-highlight-id]");
      if (!span || !content.contains(span)) return;
      handleHighlightClick(
        {
          color: span.dataset.color,
          start: span.dataset.start,
          end: span.dataset.end,
        },
        span.dataset.highlightId,
      );
    };

    const handleOver = (e) => {
      const span = e.target.closest("[data-highlight-id]");
      if (!span || !content.contains(span)) return;
      groupSpans(span.dataset.highlightId).forEach((el) =>
        el.classList.add("shadow-lz-dark"),
      );
    };

    const handleOut = (e) => {
      const span = e.target.closest("[data-highlight-id]");
      if (!span || !content.contains(span)) return;
      groupSpans(span.dataset.highlightId).forEach((el) =>
        el.classList.remove("shadow-lz-dark"),
      );
    };

    content.addEventListener("click", handleClick);
    content.addEventListener("mouseover", handleOver);
    content.addEventListener("mouseout", handleOut);
    return () => {
      content.removeEventListener("click", handleClick);
      content.removeEventListener("mouseover", handleOver);
      content.removeEventListener("mouseout", handleOut);
    };
  }, [loading, lessonContent]);

  return (
    <div className={`flex flex-col justify-between h-[calc(100vh-4rem)]`}>
      <UtilityModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        handleDelete={deleteHighlight}
        handleUpdate={updateHighlight}
        highlightKey={currentHighlightKey}
        currentHighlight={currentHighlight}
        notesPanelOpen={notesPanelOpen}
        setNotesPanelOpen={setNotesPanelOpen}
        onOpenNotes={(position) => {
          setModalPosition(position);
          setNotesModalOpen(true);
          setCurrentNote(currentHighlight?.note || "");
          setModalOpen(false);
        }}
      />
      <NotesModal
        isOpen={notesModalOpen}
        onClose={() => setNotesModalOpen(false)}
        onSave={saveNote}
        initialNote={currentNote}
        position={modalPosition}
        containerRef={scrollContainerRef}
      />
      <div
        ref={scrollContainerRef}
        className="flex flex-row h-full overflow-y-auto"
      >
        <div
          className={`flex-1 overflow-y-auto selection:text-black ${getSelectColor(
            highlightColor,
          )} ${lessonContent?.is_locked ? "pb-4" : ""}`}
        >
          {loading ? (
            <BorderBox>
              <Spinner />
            </BorderBox>
          ) : (
            <Content
              contentRef={contentRef}
              getSelectColor={getSelectColor}
              lessonContent={lessonContent}
              highlightColor={highlightColor}
              fontSize={fontSize}
              onHighlight={handleHighlightSelection}
            />
          )}
        </div>
        <div
          className={`${
            notesPanelOpen ? "w-1/4 opacity-100" : "w-0 opacity-0 hidden"
          } ml-4 overflow-hidden`}
        >
          <Notes
            onDeleteNote={deleteNote}
            onHighlightClick={scrollToHighlight}
            loading={loading}
            annotations={annotations}
            archive={archive}
            onDeleteArchivedAnnotation={deleteArchivedAnnotation}
          />
        </div>
      </div>
      {!lessonContent?.is_locked && (
        <UtilityPanel
          notesPanelOpen={notesPanelOpen}
          setNotesPanelOpen={setNotesPanelOpen}
          lessonContent={lessonContent}
        />
      )}
    </div>
  );
}
